import { Equal, Result } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import { asOfTag } from "../../api/client";
import { getTags } from "../../api/commands/tags";
import { asOfOf } from "../../app/routes";
import type { Page, PageReturn, Shared } from "../shared";
import { Message, OutMessage } from "./message";
import type { Model } from "./model";

export const FetchTags = getTags.command(Message.CompletedFetchTags, asOfTag);

type Return = PageReturn<Model, Message, OutMessage>;

/** `start` keeps Success data visible as Refreshing: v1's background refetch shows no spinner swap. */
const load = (model: Model, shared: Shared): PageReturn<Model, Message> => ({
	model: evo(model, { tags: start }),
	commands: [FetchTags({ asOf: shared.asOf, generation: shared.generation })],
});

export const page: Page<Model, Message, OutMessage> = {
	init: (_route, shared) =>
		load({ page: "Tags", tags: AsyncData.Idle() }, shared),
	changedRoute: (model, previous, next, shared) =>
		Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: load(model, shared),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			ClickedRefresh: () => load(model, shared),
			CompletedFetchTags: ({ asOf, generation, result }) => {
				if (
					generation !== shared.generation ||
					!Equal.equals(asOf, shared.asOf)
				) {
					return { model };
				}
				const wasRefresh = AsyncData.isRefreshing(model.tags);
				return {
					model: evo(model, { tags: (tags) => settle(tags, result) }),
					// A refresh that returned new data is a change the root may care about.
					...(wasRefresh && Result.isSuccess(result)
						? { outMessage: OutMessage.TagsChanged() }
						: {}),
				};
			},
		}),
};
